<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import { useAuthStore } from '@/stores/auth.store'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const token = ref<string>('')
const password = ref<string>('')
const confirmPassword = ref<string>('')
const errorMessage = ref<string | null>(null)
const isLoading = ref<boolean>(false)

onMounted(async () => {
  const tokenParam = route.query.token

  if (!tokenParam) {
    router.push({ name: 'login' })
  }

  const rawToken = Array.isArray(tokenParam) ? tokenParam[0] : tokenParam

  if (!rawToken || typeof rawToken !== 'string') {
    router.push({ name: 'login' })
    return
  }

  token.value = rawToken
})

async function handleActivation() {
  errorMessage.value = null

  if (!token.value) {
    errorMessage.value = 'Missing activation token. Please revisit your email and try again'
    return
  }

  if (password.value != confirmPassword.value) {
    errorMessage.value = 'Passwords do not match'
    return
  }
  if (password.value.length < 8) {
    errorMessage.value = 'Password must be at least 8 characters'
  }

  errorMessage.value = null
  isLoading.value = true

  try {
    await authStore.register(password.value, token.value)
    router.push({ name: 'login' })
  } catch {
    errorMessage.value = 'Activation failed'
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <main class="flex items-center justify-center min-h-screen bg-gray-50 p-8">
    <Card class="w-full max-w-sm" aria-labelledby="register-heading">
      <CardHeader>
        <CardTitle id="register-heading" class="text-2xl text-center"> Register </CardTitle>
      </CardHeader>
      <CardContent>
        <form id="register-form" @submit.prevent="handleActivation">
          <div class="grid gap-4">
            <div class="grid gap-2">
              <Label for="password">Password</Label>
              <Input
                id="password"
                v-model="password"
                type="password"
                required
                autofocus
                aria-required="true"
              />
            </div>
            <div class="grid gap-2">
              <Label for="password">Confirm Password</Label>
              <Input
                id="confirmPassword"
                v-model="confirmPassword"
                type="password"
                required
                aria-required="true"
              />
            </div>
          </div>
        </form>
        <p v-if="errorMessage" class="text-red-500 text-sm text-center">{{ errorMessage }}</p>
      </CardContent>
      <CardFooter>
        <Button type="submit" form="register-form" class="w-full" :disabled="isLoading">
          {{ isLoading ? 'Loading...' : 'Sign in' }}
        </Button>
      </CardFooter>
    </Card>
  </main>
</template>
